import { Box, Checkbox, Flex, ResponsiveTable, Text, useOpenModal } from '@modusign/design-system';
import { ExternalDocument } from '@modusign/openapi-client-external-document/generated/model';
import { format } from 'date-fns';
import { ChangeEventHandler } from 'react';
import { Link } from 'react-router-dom';

import FormatTimeText from '@app/components/FormatTimeText';
import { LabelListSummary } from '@app/features/label';
import LabelListModal from '@app/features/label/components/LabelListModal';

import DocumentStatusBadge from '../../DocumentStatusBadge';
import ExternalDocumentListItemMenuButton from '../../ExternalDocumentListItemMenuButton';

// 테이블 column이 많아 뷰포트가 줄어들었을 때 제목이 안보입니다.
// 제목이 최소 16자 보이도록 뷰포트가 줄어들 때 계약 유형, 계약 체결일, 계약 상태 순으로 column을 하나식 제거합니다.
// 자세한 계산 방식은 아래를 참고해주세요.
// 참고: https://github.com/modusign/modusign-client/pull/6277

export const SHOW_STATUS_VIEWPORT = 1108;
export const SHOW_DATE_OF_COMPLETION_VIEWPORT = 1228;
export const SHOW_CATEGORY_VIEWPORT = 1388;

interface DocumentListItemProps {
  document: ExternalDocument;
  isSelected: boolean;
  onSelect: ChangeEventHandler<HTMLInputElement>;
  onMutationSuccess: () => void;
}

export default function DocumentListItem({
  document,
  isSelected,
  onSelect,
  onMutationSuccess,
}: DocumentListItemProps) {
  const url = `/external-documents/document/${document.id}`;

  const openModal = useOpenModal();

  return (
    <ResponsiveTable.TBodyRow display="table-row">
      <Box as="td" display={['none', null, 'table-cell']}>
        <Box mx="auto" position="relative" width="16px">
          <Checkbox checked={isSelected} sx={{ mx: 'auto' }} onChange={onSelect} />
        </Box>
      </Box>
      <Box as="td" display={['none', null, 'table-cell']}>
        <Link to={url}>
          <Box px={4}>{document.registrationNumber}</Box>
        </Link>
      </Box>
      <Box as="td" width={['100%', null, 'initial']}>
        <Link to={url}>
          <Flex
            flexDirection="column"
            pl={[4, null, 7]}
            pr={4}
            py={[4, null, 5]}
            sx={{
              position: 'relative',
              '&:before': {
                content: [null, null, "''"],
                height: '30px',
                width: '1px',
                backgroundColor: 'lightGray100',
                position: 'absolute',
                top: 'calc(50% - 15px)',
                left: 0,
              },
            }}
          >
            <Text
              sx={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              {document.title}
            </Text>
            <Text
              color="textNeutralLight"
              fontSize="sm"
              mt="2px"
              sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
            >
              {document.signers.map((participant) => participant.name).join(', ')}
            </Text>
            <Box display={['block', null, 'none']} mt={1}>
              <Text
                as="time"
                color="textNeutralLight"
                dateTime={document.updatedAt}
                fontSize="sm"
                whiteSpace="pre"
              >
                <FormatTimeText dateString={document.updatedAt} />
              </Text>
            </Box>
            <Box display={['block', null, 'none']} mt={3}>
              <LabelListSummary
                labelIds={document.labels.map((l) => l.id)}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  openModal(({ isOpen, onClose }) => (
                    <LabelListModal
                      isOpen={isOpen}
                      resourceId={document.id}
                      resourceType="EXTERNAL_DOCUMENT"
                      onClose={onClose}
                    />
                  ));
                }}
              />
            </Box>
          </Flex>
        </Link>
      </Box>
      <Box
        as="td"
        display="none"
        sx={{
          [`@media screen and (min-width: ${SHOW_CATEGORY_VIEWPORT}px)`]: {
            display: 'table-cell',
          },
        }}
      >
        <Box
          px={4}
          sx={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          <LabelListSummary
            labelIds={document.labels.map((l) => l.id)}
            onClick={(e) => {
              e.stopPropagation();
              openModal(({ isOpen, onClose }) => (
                <LabelListModal
                  isOpen={isOpen}
                  resourceId={document.id}
                  resourceType="EXTERNAL_DOCUMENT"
                  onClose={onClose}
                />
              ));
            }}
          />
        </Box>
      </Box>
      <Box
        as="td"
        display="none"
        sx={{
          [`@media screen and (min-width: ${SHOW_DATE_OF_COMPLETION_VIEWPORT}px)`]: {
            display: 'table-cell',
          },
        }}
      >
        <Link to={url}>
          {document.dateOfCompletion ? (
            <Text
              as="time"
              dateTime={document.dateOfCompletion}
              mx={4}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {format(new Date(document.dateOfCompletion), 'yyyy.MM.dd')}
            </Text>
          ) : (
            <Text mx={4}>-</Text>
          )}
        </Link>
      </Box>
      <Box
        as="td"
        display="none"
        sx={{
          [`@media screen and (min-width: ${SHOW_STATUS_VIEWPORT}px)`]: {
            display: 'table-cell',
          },
        }}
      >
        <Link to={url}>
          <Box px={4}>
            {document.status ? <DocumentStatusBadge status={document.status} /> : '-'}
          </Box>
        </Link>
      </Box>
      <Box as="td" display={['none', null, 'table-cell']}>
        <Link to={url}>
          <Text
            as="time"
            color="textNeutralLight"
            dateTime={document.updatedAt}
            display="block"
            fontSize="sm"
            px={4}
            whiteSpace="pre"
          >
            <FormatTimeText dateString={document.updatedAt} />
          </Text>
        </Link>
      </Box>
      <Box
        as="td"
        sx={{
          p: 6,
          position: 'relative',
          '&:before': {
            content: [null, null, "''"],
            height: '30px',
            width: '1px',
            backgroundColor: 'lightGray100',
            position: 'absolute',
            top: 'calc(50% - 15px)',
            left: 0,
          },
        }}
      >
        <ExternalDocumentListItemMenuButton
          context="LIST_PAGE"
          externalDocument={document}
          onMutationSuccess={onMutationSuccess}
        />
      </Box>
    </ResponsiveTable.TBodyRow>
  );
}
